import type { IconType } from "react-icons";
import {
  FaInfoCircle,
  FaPaperPlane,
  FaTasks,
  FaUserCheck,
  FaUserTie,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";

// Stage 1 of a two-stage procedure: a forespørsel om å delta carries the ESPD and the
// qualification answers — no price, no offer documents, no signing (those belong to stage 2).
export const REQUEST_WIZARD_STEPS = [
  { key: "intro", icon: FaInfoCircle },
  { key: "supplier", icon: FaUserTie },
  { key: "exclusion", icon: FaUserCheck },
  { key: "qualification", icon: FaTasks },
  { key: "review", icon: FaPaperPlane },
] as const satisfies ReadonlyArray<{ key: string; icon: IconType }>;

export type RequestStep = (typeof REQUEST_WIZARD_STEPS)[number]["key"];

export const REQUEST_WIZARD_STEP_KEYS = REQUEST_WIZARD_STEPS.map((s) => s.key) as readonly RequestStep[];

export const useRequestStepLabel = (): ((key: RequestStep) => string) => {
  const { t } = useTranslation();
  return (key) => {
    switch (key) {
      case "intro":
        return t("About the procurement");
      case "supplier":
        return t("About the supplier");
      case "exclusion":
        return t("Grounds for exclusion");
      case "qualification":
        return t("Qualification requirements");
      case "review":
        return t("Summary and submission");
    }
  };
};

export const requestStepNeighbors = (
  current: RequestStep,
): { previous: RequestStep | undefined; next: RequestStep | undefined } => {
  const idx = REQUEST_WIZARD_STEP_KEYS.indexOf(current);
  return {
    previous: idx > 0 ? REQUEST_WIZARD_STEP_KEYS[idx - 1] : undefined,
    next: idx >= 0 && idx < REQUEST_WIZARD_STEP_KEYS.length - 1 ? REQUEST_WIZARD_STEP_KEYS[idx + 1] : undefined,
  };
};

export const useRequestStepShortLabel = (): ((key: RequestStep) => string) => {
  const { t } = useTranslation();
  return (key) => {
    switch (key) {
      case "intro":
        return t("Overview");
      case "supplier":
        return t("Supplier");
      case "exclusion":
        return t("Exclusion grounds");
      case "qualification":
        return t("Qualification");
      case "review":
        return t("Summary");
    }
  };
};
